import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight, ArrowLeft, Upload, CheckCircle2, Trash2, FileText, Building2, ScrollText, Landmark } from "lucide-react";
import { useTenant } from "../../contexts/TenantContext";
import { BrandButton } from "./TenantShell";
import { tapi, apiErr } from "../../lib/tenant";

const ACCEPT = ".pdf,.xlsx,.xlsm,.csv,.docx,.pptx,.txt,.jpg,.jpeg,.png,.heic";

const FIN_TYPES = [
  { v: "bank_statements", l: "Bank statement" },
  { v: "mpesa", l: "M-Pesa statement", doc_type: "bank_statements", label: "M-Pesa statement" },
  { v: "mgmt_accounts", l: "Management accounts" },
  { v: "audited_accounts", l: "Audited financials" },
];

export const LPO_SECTIONS = [
  { key: "certificate", title: "Company Certificate", help: "Certificate of incorporation or business registration.", Icon: Building2, types: ["legal"], doc_type: "legal" },
  { key: "lpo", title: "LPO", help: "The signed Local Purchase Order you need funded. Add any supporting contract too.", Icon: ScrollText, types: ["purchase_orders"], doc_type: "purchase_orders" },
  { key: "financials", title: "Financial Documents", help: "Management accounts, audited financials, bank statements or M-Pesa statements — upload as many as you have.", Icon: Landmark, types: ["bank_statements", "mgmt_accounts", "audited_accounts"], picker: true },
];

function Section({ sec, files, onUpload, onRemove, busy }) {
  const [finType, setFinType] = useState("bank_statements");
  const [pw, setPw] = useState("");
  const done = files.length > 0;
  const chosen = FIN_TYPES.find((t) => t.v === finType);
  const fileLabel = (d) => d.custom_label || FIN_TYPES.find((t) => t.v === d.doc_type)?.l || "";
  return (
    <li className="rounded-2xl border border-neutral-200 bg-white p-5" data-testid={`lpo-docs-section-${sec.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className={"flex h-10 w-10 shrink-0 items-center justify-center rounded-xl " + (done ? "text-white" : "bg-neutral-100 text-neutral-400")} style={done ? { background: "var(--tenant)" } : undefined}>
            {done ? <CheckCircle2 size={20} /> : <sec.Icon size={20} />}
          </span>
          <div>
            <p className="text-base font-extrabold">{sec.title}</p>
            <p className="text-xs text-neutral-500">{sec.help}</p>
            <p className="mt-1 text-[11px] font-semibold" style={{ color: done ? "var(--tenant)" : "#a3a3a3" }} data-testid={`lpo-docs-status-${sec.key}`}>
              {done ? `${files.length} file${files.length > 1 ? "s" : ""} uploaded` : "Required · nothing uploaded yet"}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {sec.picker && (
            <select value={finType} onChange={(e) => setFinType(e.target.value)} className="rounded-full border border-neutral-300 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700" data-testid="lpo-docs-fin-type">
              {FIN_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          )}
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-neutral-300 px-3 py-1.5 text-xs font-bold text-neutral-700 hover:bg-neutral-50">
            {busy === sec.key ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {done ? "Add another" : "Upload"}
            <input type="file" multiple accept={ACCEPT} className="hidden" disabled={!!busy}
              onChange={(e) => { onUpload(sec, Array.from(e.target.files || []), sec.picker ? chosen : null, pw); e.target.value = ""; }}
              data-testid={`lpo-docs-input-${sec.key}`} />
          </label>
        </div>
      </div>
      {sec.picker && (finType === "bank_statements" || finType === "mpesa") && (
        <input value={pw} onChange={(e) => setPw(e.target.value)} placeholder="PDF password (only if your statement is locked)" className="mt-3 w-full rounded-lg border border-neutral-200 px-3 py-1.5 text-xs outline-none" data-testid="lpo-docs-pdf-password" />
      )}
      {done && (
        <ul className="mt-3 space-y-1">
          {files.map((d) => (
            <li key={d.doc_id} className="flex items-center justify-between gap-3 rounded-lg bg-neutral-50 px-3 py-1.5 text-xs" data-testid={`lpo-docs-file-${d.doc_id}`}>
              <span className="flex min-w-0 items-center gap-2"><FileText size={12} className="shrink-0 text-neutral-400" /><span className="truncate">{d.filename}</span>{sec.picker && fileLabel(d) && <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-neutral-500">{fileLabel(d)}</span>}</span>
              <button type="button" onClick={() => onRemove(d)} className="text-neutral-400 hover:text-red-500" data-testid={`lpo-docs-remove-${d.doc_id}`}><Trash2 size={13} /></button>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export const StepLpoDocuments = ({ documents, onChanged, onBack, onSubmit }) => {
  const { sub, config } = useTenant();
  const [busy, setBusy] = useState("");

  const filesFor = (sec) => documents.filter((d) => sec.types.includes(d.doc_type));
  const allDone = LPO_SECTIONS.every((s) => filesFor(s).length > 0);
  const missing = LPO_SECTIONS.filter((s) => !filesFor(s).length).map((s) => s.title);

  const upload = async (sec, files, fin, pw) => {
    if (!files.length) return;
    setBusy(sec.key);
    const docType = fin ? (fin.doc_type || fin.v) : sec.doc_type;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file); fd.append("doc_type", docType);
      if (pw && docType === "bank_statements") fd.append("pdf_password", pw);
      try {
        const { data } = await tapi.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (fin?.label) await tapi.post(`/t/${sub}/documents/${data.doc_id}/label`, { label: fin.label });
        toast.success(`${file.name} uploaded`);
      } catch (e) {
        const code = e.response?.data?.detail?.code;
        toast.error(code === "pdf_encrypted" ? "This PDF is password-protected — enter the password under Financial Documents and try again." : apiErr(e, `Couldn't upload ${file.name}`));
      }
    }
    await onChanged(); setBusy("");
  };
  const remove = async (d) => {
    try { await tapi.delete(`/documents/${d.doc_id}`); await onChanged(); } catch (e) { toast.error(apiErr(e, "Couldn't remove file")); }
  };

  return (
    <div className="space-y-5" data-testid="tenant-step-lpo-documents">
      <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-extrabold">Upload your documents</h2>
        <p className="text-sm text-neutral-500">{config.name} needs one or more files in each of the three sections below. PDF, Excel, Word or a clear photo all work — you can add several files to any section. Every file is saved the moment it uploads.</p>
      </div>
      <ul className="space-y-3">{LPO_SECTIONS.map((s) => <Section key={s.key} sec={s} files={filesFor(s)} onUpload={upload} onRemove={remove} busy={busy} />)}</ul>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-500 hover:text-neutral-800" data-testid="lpo-docs-back"><ArrowLeft size={14} /> Back</button>
        <div className="flex flex-wrap items-center gap-3">
          {!allDone && <span className="text-[11px] text-neutral-500" data-testid="lpo-docs-missing">Still needed: {missing.join(", ")}</span>}
          {allDone && (
            <BrandButton onClick={onSubmit} disabled={!!busy} data-testid="lpo-docs-submit">Submit Application <ArrowRight size={16} /></BrandButton>
          )}
        </div>
      </div>
    </div>
  );
};
